import type { AdSlot, EditorBlock, FeatureFlag, MarketingSection, NavigationItem, PublicPlatformConfig } from '../types'
import { initialAdSlots, initialFeatureFlags, initialMarketingSections, initialNavigation, initialPageBlocks } from './adminData'
import { createClientId } from '../core/id'
import { defaultManagedPageCopy, sanitizeManagedPageCopy, type ManagedPageCopy } from '../content/managedPageCopy'

async function request<T>(url: string, init: RequestInit = {}) {
  const response = await fetch(url, { credentials: 'include', ...init, headers: { Accept: 'application/json', ...init.headers } })
  const body = await response.json().catch(() => ({})) as T & { error?: string }
  if (!response.ok) throw new Error(body.error || '요청을 처리하지 못했습니다.')
  return body
}

export interface AdminStaff {
  staff_id: string
  account_id: string
  organization_id: string
  name: string
  phone: string
  email: string
  status: string
}

export interface AdminDocument {
  document_id: string
  drive_file_id: string
  title: string
  category: string
  visibility: string
  source_name: string
  uploaded_by: string
  folder_path: string
  created_at: string
}

export interface AdminAccount {
  accountId: string
  loginId: string
  name: string
  email: string
  roles: string[]
  organization: string
  status: string
  emailVerified: boolean
}

export interface DriveConnectionStatus {
  configured: boolean
  connected: boolean
  ready: boolean
  connectedEmail: string
  connectedAt: string
  rootFolderId: string
  rootFolderName: string
  rootFolderUrl: string
  redirectUri: string
  errorCode: string
  error: string
}

export interface AdminConfig {
  publicConfig: PublicPlatformConfig
  pageDraft: MarketingSection[]
  pageCopyDraft: ManagedPageCopy
  adDraft: AdSlot[]
  staff: AdminStaff[]
  permissions: Array<Record<string, string>>
  products: Array<Record<string, string>>
  logs: Array<{ id: string; at: string; actor: string; action: string; target: string; ip: string; result: string; kind: string }>
  documents: AdminDocument[]
  cases: Array<{ id: string; title: string; status: string; shareState: string; visitAt: string; updatedAt: string; pets: string[] }>
}

const localPreviewEnabled = import.meta.env.DEV

const copy = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const previewAdminConfig: AdminConfig = {
  publicConfig: {
    serviceName: 'Animal Care',
    serviceDescription: '반려동물의 변화를 기록하고 진료에 필요한 내용을 안전하게 전달하는 서비스',
    canonicalOrigin: 'https://myanimal.care',
    pageBlocks: copy(initialPageBlocks),
    pageSections: copy(initialMarketingSections),
    pageCopy: copy(defaultManagedPageCopy),
    navigation: copy(initialNavigation),
    adSlots: copy(initialAdSlots),
    featureFlags: copy(initialFeatureFlags),
    products: [],
  },
  pageDraft: copy(initialMarketingSections),
  pageCopyDraft: copy(defaultManagedPageCopy),
  adDraft: copy(initialAdSlots),
  staff: [],
  permissions: [],
  products: [],
  logs: [{ id: 'preview-log', at: new Date().toISOString(), actor: '미리보기 관리자', action: '운영 화면 확인', target: '로컬 미리보기', ip: '비공개', result: 'success', kind: 'access' }],
  documents: [],
  cases: [],
}
let previewAccounts: AdminAccount[] = [{
  accountId: 'preview-hospital-account',
  loginId: 'clinic-preview',
  name: '동물병원 담당자',
  email: '',
  roles: ['병원 관리자'],
  organization: '미리보기 동물병원',
  status: 'invited',
  emailVerified: false,
}]

function savePreviewSection(section: string, value: unknown) {
  if (section === 'pageDraft') previewAdminConfig.pageDraft = copy(value as MarketingSection[])
  if (section === 'pagePublish') {
    previewAdminConfig.pageDraft = copy(value as MarketingSection[])
    previewAdminConfig.publicConfig.pageSections = copy(value as MarketingSection[])
  }
  if (section === 'pageCopyDraft') previewAdminConfig.pageCopyDraft = sanitizeManagedPageCopy(value)
  if (section === 'pageCopyPublish') {
    previewAdminConfig.pageCopyDraft = sanitizeManagedPageCopy(value)
    previewAdminConfig.publicConfig.pageCopy = sanitizeManagedPageCopy(value)
  }
  if (section === 'pageBlocks') previewAdminConfig.publicConfig.pageBlocks = copy(value as EditorBlock[])
  if (section === 'navigation') previewAdminConfig.publicConfig.navigation = copy(value as NavigationItem[])
  if (section === 'featureFlags') previewAdminConfig.publicConfig.featureFlags = copy(value as FeatureFlag[])
  if (section === 'adSlots') {
    const payload = value as { slots?: AdSlot[] }
    previewAdminConfig.publicConfig.adSlots = copy(payload.slots || [])
  }
  if (section === 'adDraft') previewAdminConfig.adDraft = copy(value as AdSlot[])
  if (section === 'adPublish') {
    previewAdminConfig.adDraft = copy(value as AdSlot[])
    previewAdminConfig.publicConfig.adSlots = copy(value as AdSlot[])
  }
  if (section === 'staff') previewAdminConfig.staff = copy(value as AdminStaff[])
  if (section === 'permissions') previewAdminConfig.permissions = copy(value as Array<Record<string, string>>)
  if (section === 'commerce') previewAdminConfig.products = copy(value as Array<Record<string, string>>)
  if (section === 'settings') {
    const settings = value as Record<string, string>
    previewAdminConfig.publicConfig = {
      ...previewAdminConfig.publicConfig,
      serviceName: settings.service_name || previewAdminConfig.publicConfig.serviceName,
      serviceDescription: settings.service_description || previewAdminConfig.publicConfig.serviceDescription,
      canonicalOrigin: settings.canonical_origin || previewAdminConfig.publicConfig.canonicalOrigin,
    }
  }
}

export async function loadPublicConfig(signal?: AbortSignal) {
  return request<PublicPlatformConfig>('/api/public-config', { signal })
}

export async function loadAdminConfig(signal?: AbortSignal) {
  if (localPreviewEnabled) return copy(previewAdminConfig)
  return request<AdminConfig>('/api/admin/config', { signal })
}

export async function saveAdminSection(section: string, value: unknown, csrf: string) {
  if (localPreviewEnabled) {
    savePreviewSection(section, value)
    return { ok: true as const, savedAt: new Date().toISOString() }
  }
  return request<{ ok: true; savedAt: string }>('/api/admin/config', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ section, value }),
  })
}

export async function uploadSiteAsset(file: File, csrf: string) {
  if (localPreviewEnabled) return { fileId: '', name: file.name, previewUrl: URL.createObjectURL(file), publicUrl: '' }
  const form = new FormData()
  form.set('file', file)
  return request<{ fileId: string; name: string; previewUrl: string; publicUrl: string }>('/api/admin/site-asset', { method: 'POST', headers: { 'X-CSRF-Token': csrf }, body: form })
}

export async function uploadAdminDocument(form: FormData, csrf: string) {
  if (localPreviewEnabled) {
    const file = form.get('file') as File
    const document: AdminDocument = {
      document_id: createClientId('preview-document-'),
      drive_file_id: '',
      title: String(form.get('title') || file?.name || '미리보기 문서'),
      category: String(form.get('category') || '운영 문서'),
      visibility: String(form.get('visibility') || 'super_admin'),
      source_name: file?.name || '미리보기 파일',
      uploaded_by: 'preview-super-admin',
      folder_path: '로컬 미리보기 · 실제 업로드 없음',
      created_at: new Date().toISOString(),
    }
    previewAdminConfig.documents = [document, ...previewAdminConfig.documents]
    return { document: copy(document) }
  }
  return request<{ document: AdminDocument }>('/api/admin/document', { method: 'POST', headers: { 'X-CSRF-Token': csrf }, body: form })
}

export async function deleteAdminDocument(id: string, csrf: string) {
  if (localPreviewEnabled) {
    previewAdminConfig.documents = previewAdminConfig.documents.filter((item) => item.document_id !== id)
    return { ok: true as const }
  }
  return request<{ ok: true }>('/api/admin/document', { method: 'DELETE', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf }, body: JSON.stringify({ id }) })
}

export async function loadAdminAccounts() {
  if (localPreviewEnabled) return { accounts: copy(previewAccounts) }
  return request<{ accounts: AdminAccount[] }>('/api/admin/accounts')
}

export async function inviteAdminAccount(input: { loginId: string; name: string; email: string; organization: string; roles: string[] }, csrf: string) {
  if (localPreviewEnabled) {
    const labels: Record<string, string> = { member: '회원', hospital_admin: '병원 관리자', platform_operator: '플랫폼 담당자' }
    const account: AdminAccount = { accountId: createClientId('preview-account-'), ...copy(input), roles: input.roles.map((role) => labels[role] || role), status: 'invited', emailVerified: false }
    previewAccounts = [account, ...previewAccounts]
    return { accountId: account.accountId, status: 'preview_invitation' }
  }
  return request<{ accountId: string; status: string }>('/api/admin/accounts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(input),
  })
}

export async function loadDriveConnectionStatus(signal?: AbortSignal) {
  if (localPreviewEnabled) return {
    configured: true,
    connected: false,
    ready: false,
    connectedEmail: '',
    connectedAt: '',
    rootFolderId: '',
    rootFolderName: '',
    rootFolderUrl: '',
    redirectUri: `${window.location.origin}/api/admin/drive/callback`,
    errorCode: '',
    error: '',
  } satisfies DriveConnectionStatus
  return request<DriveConnectionStatus>('/api/admin/drive', { signal })
}

export async function startDriveConnection(csrf: string) {
  if (localPreviewEnabled) return { authorizationUrl: '#drive-preview', redirectUri: `${window.location.origin}/api/admin/drive/callback` }
  return request<{ authorizationUrl: string; redirectUri: string }>('/api/admin/drive', { method: 'POST', headers: { 'X-CSRF-Token': csrf } })
}

export async function disconnectDriveConnection(csrf: string) {
  if (localPreviewEnabled) return { ok: true as const }
  return request<{ ok: true }>('/api/admin/drive', { method: 'DELETE', headers: { 'X-CSRF-Token': csrf } })
}

export function mergePlatformConfig(config: PublicPlatformConfig | null, fallback: { pageBlocks: EditorBlock[]; navigation: NavigationItem[]; adSlots: AdSlot[]; featureFlags: FeatureFlag[] }): PublicPlatformConfig {
  return {
    serviceName: config?.serviceName || 'Animal Care',
    serviceDescription: config?.serviceDescription || '반려동물의 변화를 기록하고 진료에 필요한 내용을 안전하게 전달하는 서비스',
    canonicalOrigin: config?.canonicalOrigin || 'https://myanimal.care',
    pageBlocks: config?.pageBlocks?.length ? config.pageBlocks : fallback.pageBlocks,
    pageSections: config?.pageSections?.length ? config.pageSections : initialMarketingSections,
    pageCopy: sanitizeManagedPageCopy(config?.pageCopy || defaultManagedPageCopy),
    navigation: config?.navigation?.length ? config.navigation : fallback.navigation,
    adSlots: config?.adSlots?.length ? config.adSlots : fallback.adSlots,
    featureFlags: config?.featureFlags?.length ? config.featureFlags : fallback.featureFlags,
    products: config?.products || [],
  }
}
